import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface Estimate {
  id: string;
  estimate_number: string;
  customer_id: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string;
  customer_address: string;
  city: string;
  county: string;
  zip_code: string;
  status: string;
  price_book_id: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  valid_until: string | null;
  notes: string | null;
  measurement_file_url: string | null;
  measurement_source: string | null;
  measurement_data: any | null;
  measurement_pdf_url: string | null;
  project_photos: any[];
  created_at: string;
  updated_at: string;
  created_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
}

export const useEstimates = () => {
  const queryClient = useQueryClient();

  const { data: estimates, isLoading } = useQuery({
    queryKey: ["estimates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimates")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Estimate[];
    },
  });

  const createEstimate = useMutation({
    mutationFn: async (estimate: Partial<Estimate>) => {
      const { data, error } = await supabase
        .from("estimates")
        .insert([estimate as any])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["estimates"] });
      toast({ title: "Estimate created successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create estimate",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateEstimate = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Estimate> & { id: string }) => {
      const { data, error } = await supabase
        .from("estimates")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["estimates"] });
      toast({ title: "Estimate updated successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update estimate",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteEstimate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("estimates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["estimates"] });
      toast({ title: "Estimate deleted successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete estimate",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    estimates,
    isLoading,
    createEstimate,
    updateEstimate,
    deleteEstimate,
  };
};
